'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import keyMirror from 'react-utils/keyMirror';
import Settings from 'react-codemirror/model/Settings';
import { UISELECT_THEME_OPTIONS } from 'react-codemirror/util/Theme';
import { CODE } from 'DesignData/enums/settings/Categories';
import { HUBL_HTML, HUBL_CSS, HUBL_JS } from 'DesignData/constants/TemplateFileTypes';
import { DEMO_HUBL_CODE, DEMO_JS_CODE, DEMO_CSS_CODE } from 'DesignData/constants/settings/codemirror/Demo';
import UIButton from 'UIComponents/button/UIButton';
import UIInputStaticLabel from 'UIComponents/input/UIInputStaticLabel';
import UISelect from 'UIComponents/input/UISelect';
import UIStepperInput from 'UIComponents/input/UIStepperInput';
import UIToggle from 'UIComponents/input/UIToggle';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBox from 'UIComponents/layout/UIBox';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import Accordion from 'DesignManagerIde/components/settings/inputs/Accordion';
import InputWrapper from 'DesignManagerIde/components/settings/inputs/InputWrapper';
import IDECodeMirror from 'DesignEditorsUI/components/shared/IDECodeMirror';
import TemplateFileTypeToMode from 'DesignData/enums/TemplateFileTypeToMode';
import CodeAccordion from 'DesignComponents/inputs/codeAccordion/CodeAccordion';
import CodeAccordionItem from 'DesignComponents/inputs/codeAccordion/CodeAccordionItem';
const INPUT_TYPE = keyMirror({
  BOOLEAN: null,
  SELECT: null,
  PIXEL: null,
  NUMBER: null
});
const DEMO_CODE_BY_TYPE = {
  [HUBL_HTML]: DEMO_HUBL_CODE,
  [HUBL_JS]: DEMO_JS_CODE,
  [HUBL_CSS]: DEMO_CSS_CODE
};

const flipBooleanCoerce = bool => !bool;

const i18nOption = (optionName, field) => {
  return `designManager.settings.categories.${CODE}.fields.${optionName}.${field}`;
};

const i18nCategory = (categoryName, field) => {
  return `designManager.settings.categories.${CODE}.categories.${categoryName}.${field}`;
};

export default class Code extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      activeDemo: HUBL_HTML
    };

    this.changeDemoType = e => {
      this.setState({
        activeDemo: e.target.value
      });
    };
  }

  componentDidMount() {
    this.registerToolbarContent();
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.isDefaultSettings !== nextProps.isDefaultSettings) {
      return true;
    }

    if (this.state.activeDemo !== nextState.activeDemo) {
      return true;
    }

    const {
      settings: prevSettings
    } = this.props;
    const {
      settings
    } = nextProps;

    if (prevSettings === settings) {
      return false;
    }

    return Settings.DEFAULT_FIELDS.some(({
      name
    }) => settings[name] !== prevSettings[name]);
  }

  componentDidUpdate() {
    this.registerToolbarContent();
  }

  registerToolbarContent() {
    this.props.registerToolbarContent({
      leftContent: this.renderResetButton()
    });
  }

  getOrCreateOnChange(name, type, coerce) {
    const key = `__onChangeSetting_${name}`;

    if (this[key]) {
      return this[key];
    }

    this[key] = ({
      target: {
        value,
        checked
      }
    }) => {
      let val = type === INPUT_TYPE.BOOLEAN ? checked : value;

      if (coerce) {
        val = coerce(val);
      }

      this.props.update({
        [name]: val
      });
    };

    return this[key];
  }

  getValueForSetting(name) {
    return this.props.settings[name];
  }

  get codemirrorComponentProps() {
    this.__settingsModel = this.__settingsModel || new Settings();
    Object.assign(this.__settingsModel, this.props.settings);
    return this.__settingsModel.componentProps;
  }

  renderI18nOptionMessage(optionName, field) {
    return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: i18nOption(optionName, field)
    });
  }
  /**
   * Render the input wrapper component with label and help message.
   *
   * @param {String}  name - Settings field name
   * @param {Node}    input
   */


  renderInputWrapper(name, input) {
    const label = this.renderI18nOptionMessage(name, 'label');
    const help = this.renderI18nOptionMessage(name, 'help');
    return /*#__PURE__*/_jsx(InputWrapper, {
      label: label,
      description: help,
      children: input
    }, name);
  }
  /**
   * Renders the input component.
   *
   * @param {String}      name - Settings field name
   * @param {INPUT_TYPE}  type
   * @param {Object}      options
   */


  renderInput(name, type, options) {
    switch (type) {
      case INPUT_TYPE.BOOLEAN:
        return this.renderBooleanInput(name, options);

      case INPUT_TYPE.NUMBER:
        return this.renderNumberInput(name, options);

      case INPUT_TYPE.PIXEL:
        return this.renderPixelInput(name, options);

      case INPUT_TYPE.SELECT:
        return this.renderSelectInput(name, options);

      default:
        return null;
    }
  }
  /**
   * Renders the input component within the input wrapper component.
   *
   * @param {String}      name - Settings field name
   * @param {INPUT_TYPE}  type
   * @param {Object}      options
   */


  renderInputWithWrapper(name, type, options) {
    const input = this.renderInput(name, type, options);
    return this.renderInputWrapper(name, input);
  }

  renderForm() {
    return [this.renderAppearanceForm(), this.renderSourceForm()];
  }

  renderCategoryLabel(category) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nCategory(category, 'label')
    });
  }

  renderInlineFontInputs() {
    return /*#__PURE__*/_jsxs(UIFlex, {
      className: "settings-inline-inputs",
      align: "center",
      justify: "start",
      direction: "row",
      wrap: "nowrap",
      children: [/*#__PURE__*/_jsx(UIBox, {
        grow: 1,
        shrink: 0,
        children: this.renderInput('fontFamily', INPUT_TYPE.SELECT, {
          searchable: false,
          // TODO: Move to react-codemirror and use Google Fonts that Bill added.
          options: [{
            value: 'Source Code Pro',
            text: 'Source Code Pro'
          }, {
            value: 'monospace',
            text: 'monospace'
          }]
        })
      }), /*#__PURE__*/_jsx(UIBox, {
        children: this.renderInput('fontSize', INPUT_TYPE.PIXEL, {
          min: 8,
          max: 24,
          stepSize: 1,
          className: 'settings-input-font-size'
        })
      })]
    });
  }

  renderAppearanceForm() {
    const key = 'APPEARANCE';
    return /*#__PURE__*/_jsxs(Accordion, {
      title: this.renderCategoryLabel(key),
      children: [this.renderInputWithWrapper('theme', INPUT_TYPE.SELECT, {
        searchable: true,
        options: UISELECT_THEME_OPTIONS,
        autoFocus: true
      }), this.renderInputWrapper('font', this.renderInlineFontInputs())]
    }, key);
  }

  renderSourceForm() {
    const key = 'SOURCE';
    return /*#__PURE__*/_jsxs(Accordion, {
      title: this.renderCategoryLabel(key),
      children: [this.renderInputWithWrapper('tabSize', INPUT_TYPE.NUMBER, {
        min: 1,
        max: 10,
        stepSize: 1
      }), this.renderInputWithWrapper('indentUnit', INPUT_TYPE.NUMBER, {
        min: 1,
        max: 10,
        stepSize: 1
      }), this.renderInputWithWrapper('disableSoftTabs', INPUT_TYPE.BOOLEAN, {
        coerce: flipBooleanCoerce
      }), this.renderInputWithWrapper('smartIndent', INPUT_TYPE.BOOLEAN), this.renderInputWithWrapper('electricChars', INPUT_TYPE.BOOLEAN), this.renderInputWithWrapper('indentWithTabs', INPUT_TYPE.BOOLEAN), this.renderInputWithWrapper('lineWrapping', INPUT_TYPE.BOOLEAN), this.renderInputWithWrapper('autoCloseBrackets', INPUT_TYPE.BOOLEAN), this.renderInputWithWrapper('autoCloseTags', INPUT_TYPE.BOOLEAN), this.renderInputWithWrapper('matchBrackets', INPUT_TYPE.BOOLEAN), this.renderInputWithWrapper('matchTags', INPUT_TYPE.BOOLEAN), this.renderInputWithWrapper('continueComments', INPUT_TYPE.BOOLEAN)]
    }, key);
  }

  renderNumberInput(name, options) {
    const value = this.getValueForSetting(name);
    return /*#__PURE__*/_jsx(UIStepperInput, Object.assign({
      onChange: this.getOrCreateOnChange(name, INPUT_TYPE.NUMBER),
      value: value
    }, options), name);
  }

  renderPixelInput(name, options) {
    return /*#__PURE__*/_jsx(UIInputStaticLabel, {
      text: "px",
      position: "end",
      children: this.renderNumberInput(name, options)
    }, name);
  }

  renderSelectInput(name, options) {
    const selectedValue = this.getValueForSetting(name);
    return /*#__PURE__*/_jsx(UISelect, Object.assign({
      onChange: this.getOrCreateOnChange(name, INPUT_TYPE.SELECT),
      value: selectedValue
    }, options), name);
  }

  renderBooleanInput(name, options) {
    const value = this.getValueForSetting(name);

    const _ref = options || {},
          {
      coerce
    } = _ref,
          props = _objectWithoutPropertiesLoose(_ref, ["coerce"]);

    return /*#__PURE__*/_jsx(UIToggle, Object.assign({
      onChange: this.getOrCreateOnChange(name, INPUT_TYPE.BOOLEAN, coerce),
      size: "extra-small",
      checked: coerce ? coerce(value) : value
    }, props), name);
  }

  renderResetButton() {
    return /*#__PURE__*/_jsx(UIButton, {
      size: "extra-small",
      onClick: this.props.reset,
      disabled: this.props.isDefaultSettings,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `designManager.settings.categories.${CODE}.reset.title`
      })
    });
  }

  renderCodeMirror(type) {
    return /*#__PURE__*/_jsx(IDECodeMirror, Object.assign({}, this.codemirrorComponentProps, {
      codemirrorType: type,
      sessionId: `settings-demo-${type}`,
      mode: TemplateFileTypeToMode[type],
      value: DEMO_CODE_BY_TYPE[type],
      disableSession: true
    }));
  }

  renderCodeAccordionItem(type) {
    return /*#__PURE__*/_jsx(CodeAccordionItem, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `DesignEditorsUI.codeFileTypes.${type}`
      }),
      codemirror: this.renderCodeMirror(type)
    });
  }

  render() {
    return /*#__PURE__*/_jsxs(UIFlex, {
      align: "start",
      justify: "start",
      direction: "row",
      className: "settings-editor settings-code-editor",
      children: [/*#__PURE__*/_jsx(UIBox, {
        className: "settings-code-categories",
        children: this.renderForm()
      }), /*#__PURE__*/_jsx(UIFlex, {
        align: "stretch",
        className: "settings-code-scroll-column",
        direction: "column",
        wrap: "wrap",
        children: /*#__PURE__*/_jsxs(CodeAccordion, {
          id: "settings-code-demo",
          children: [this.renderCodeAccordionItem(HUBL_HTML), this.renderCodeAccordionItem(HUBL_CSS), this.renderCodeAccordionItem(HUBL_JS)]
        })
      })]
    });
  }

}
Code.propTypes = {
  settings: Settings.shapePropType.isRequired,
  update: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  isDefaultSettings: PropTypes.bool.isRequired,
  registerToolbarContent: PropTypes.func.isRequired
};